import { getApiErrorMessage } from '@components/common/util/util';
import { apiAuthCaller } from '@services/apiCaller';
import { action, observable, makeObservable } from 'mobx';

import { IRootStore } from '@stores/index';
import {
  CUSTOM_ACTIVATION_DESTINATION_DEFINITION_NAME,
  DESTINATION_DEFINITION_CATEGORY,
} from '@components/common/constants';
import { Analytics, webapp } from '@lib/analytics';
import { DestinationStore, IDestination, IDestinationStore } from './destination';
import DraftDestination from './draftDestination';
import { getPermissions } from './util';
import { PendingTask } from './user';
import { isOauthEnabledDest } from '@components/common/util/authUtil';

interface CreatePayload {
  name: string;
  allowDuplicateNames?: boolean;
  definitionName: string;
  destinationDefinitionId: string;
  config: {
    webhookUrl?: string;
  };
  enabled?: boolean;
}

export type ServerDestination = Pick<
  IDestination,
  | 'id'
  | 'name'
  | 'enabled'
  | 'config'
  | 'secretConfig'
  | 'destinationDefinition'
  | 'destinationTransformations'
  | 'version'
  | 'versionInfo'
> & {
  permissions?: { id: string; isLocked: boolean };
};

export interface IDestinationsListStore {
  destinations: IDestinationStore[];
  firstLoad: boolean;
  draftDestination: DraftDestination;
  rootStore: IRootStore;
  setDestinations(destinations: IDestinationStore[]): void;
  getDestinations(): void;
  createDestination(
    dest: CreatePayload,
    onSuccess?: (dest: Pick<IDestinationStore, 'id' | 'enabled' | 'toggleEnabled'>) => void,
    onError?: () => void,
  ): Promise<Pick<IDestination, 'id' | 'destinationDefinition'> | undefined>;
  cloneDestination(
    name: string,
    id: string,
    onSuccess: (dest: Pick<IDestination, 'id'>) => void,
    onError: () => void,
  ): void;
  createDestinationConnections(
    dest: Pick<IDestination, 'id'>,
    sourceIds: string[],
    onSuccess?: () => void,
    sqlModelId?: string,
    onError?: () => void,
  ): Promise<boolean>;
  deleteDestination(dest: IDestination, onSuccess: () => void, onError?: () => void): Promise<void>;
  destinationById(destId: string): IDestinationStore | undefined;
  hasWareHouseDestinations(): boolean;
  checkDestinationNameUniqueness: (newName: string, currentName?: string) => boolean;
  createDatabricksDestination(workspaceId: string): Promise<string | undefined>;
  getKeysToRemove(
    destination: Pick<IDestination, 'destinationDefinition'> &
      Partial<Pick<IDestination, 'id' | 'config'>>,
  ): Set<string>;
  reset: () => void;
}

export class DestinationsListStore implements IDestinationsListStore {
  @observable public destinations: IDestinationStore[] = [];

  @observable public rootStore: IRootStore;

  @observable public firstLoad = false;

  @observable public draftDestination: DraftDestination;

  constructor(rootStore: IRootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
    this.draftDestination = new DraftDestination();
  }

  @action.bound
  public setDestinations(destinations: IDestinationStore[]): void {
    this.destinations = destinations;
  }

  @action.bound
  public async getDestinations() {
    const { selectedWorkspaceId } = this.rootStore.userStore;
    try {
      const res = await apiAuthCaller().get<{ destinations: ServerDestination[] }>(
        `/destinations?workspace_id=${selectedWorkspaceId}`,
      );
      this.destinations = res.data.destinations.map(
        (destination) => new DestinationStore(destination, this.rootStore),
      );
      this.rootStore.permissionsStore.registerResource(getPermissions(res.data.destinations));
      this.firstLoad = true;
    } catch (err) {}
  }

  @action.bound
  public async createDestination(
    dest: CreatePayload,
    onSuccess?: (dest: Pick<IDestinationStore, 'id' | 'enabled' | 'toggleEnabled'>) => void,
    onError?: () => void,
  ) {
    const { messagesStore, workspaceStore, destinationDefsListStore } = this.rootStore;
    try {
      const version = destinationDefsListStore.getCreateDestinationDef(dest.destinationDefinitionId)
        ?.version;
      const { data } = await apiAuthCaller().post<ServerDestination>('/destinations/', {
        name: dest.name,
        destinationDefinitionId: dest.destinationDefinitionId,
        config: dest.config,
        allowDuplicateNames: dest.allowDuplicateNames,
        enabled: dest.enabled ?? true,
        ...(version !== undefined && { version }),
      });
      const res = this.postDestinationCreation(data, workspaceStore.id);
      if (onSuccess) {
        onSuccess(res);
      } else {
        messagesStore.showSuccessMessage('Destination created successfully');
      }
      return res;
    } catch (err) {
      messagesStore.showErrorMessage(getApiErrorMessage(err, 'Failed to create destination'));
      onError?.();
      return undefined;
    }
  }

  private postDestinationCreation(response: ServerDestination, workspaceId: string) {
    const orgId = this.rootStore.userStore.workspaces.find((ws) => ws.id === workspaceId)
      ?.organizationId;
    const savedDest = new DestinationStore(response, this.rootStore);
    Analytics.track({
      event: 'destinationCreated',
      properties: {
        destinationId: savedDest.id,
        destinationType: savedDest.destinationDefinition.name,
        workspaceId,
        organizationId: orgId,
      },
    });
    if (workspaceId === this.rootStore.workspaceStore.id) {
      this.destinations.push(savedDest);
      this.rootStore.permissionsStore.addNewResource(savedDest.id);
    }
    return savedDest;
  }

  createDatabricksDestination(workspaceId: string) {
    const { userStore, messagesStore } = this.rootStore;
    return apiAuthCaller({ nonWorkspaceRoute: true })
      .post<ServerDestination>(`/workspaces/${workspaceId}/partners/databricks`)
      .then((res) => {
        messagesStore.showSuccessMessage(
          'Congrats. Your Databricks destination has been setup. Double-check your configurations, and add a source to complete your connection',
        );
        this.postDestinationCreation(res.data, workspaceId);
        userStore.removePendingTask(PendingTask.databricks);
        return res.data.id;
      })
      .catch((err) => {
        messagesStore.showErrorMessage(
          getApiErrorMessage(err, 'Failed to provision Databricks destination'),
        );
        return undefined;
      });
  }

  @action.bound
  public async cloneDestination(
    name: string,
    id: string,
    onSuccess: (dest: Pick<IDestination, 'id'>) => void,
    onError: () => void,
  ) {
    const { messagesStore, permissionsStore } = this.rootStore;

    try {
      const r = await apiAuthCaller().post<ServerDestination>(`/destinations/${id}/clone`, {
        name,
      });
      this.destinations.push(new DestinationStore(r.data, this.rootStore));
      Analytics.track({
        event: 'destinationCreated',
        properties: {
          destinationId: r.data.id,
          destinationType: r.data.destinationDefinition.name,
        },
      });
      permissionsStore.addNewResource(r.data.id);
      messagesStore.showSuccessMessage('Destination cloned successfully');
      onSuccess(r.data);
    } catch (e) {
      messagesStore.showErrorMessage('Failed to clone destination');
      onError();
    }
  }

  @action.bound
  public async createDestinationConnections(
    dest: IDestination,
    sourceIds: string[],
    onSuccess: () => void,
    sqlModelId?: string,
    onError?: () => void,
  ) {
    const { messagesStore, connectionsStore, modelListStore, organizationStore } = this.rootStore;
    try {
      await apiAuthCaller().post(`/destinations/${dest.id}/connect`, {
        sourceIds,
        sqlModelId,
      });

      if (sqlModelId) {
        const sqlModel = modelListStore.getModelById(sqlModelId);
        sqlModel?.setSourceIds([...sqlModel.sourceIds, sourceIds[0]]);
      }
      // update connections store
      sourceIds.forEach((_, key) => {
        if (!connectionsStore.connections[sourceIds[key]]) {
          connectionsStore.connections[sourceIds[key]] = [];
        }
        connectionsStore.connections[sourceIds[key]].push(dest.id);
        Analytics.group(organizationStore.id, { orgConnectionCreated: true });
        // FE companion to the server-side connectionCreated (thin subset — see connection_created_rule).
        // Wrapped separately so an analytics SDK error can't turn a successful connection into a UI failure.
        try {
          webapp.trackConnectionCreated({
            sourceId: sourceIds[key],
            destinationId: dest.id,
            destinationType: dest.destinationDefinition.name,
          });
        } catch {}
      });
      onSuccess?.();
      return true;
    } catch (err) {
      onError?.();
      messagesStore.showErrorMessage(
        getApiErrorMessage(err, 'Failed to connect source to destination'),
      );
      return false;
    }
  }

  @action.bound
  public async deleteDestination(
    destination: IDestinationStore,
    onSuccess: () => void,
    onError?: () => void,
  ) {
    const { messagesStore, transformationsListStore } = this.rootStore;
    try {
      await apiAuthCaller().delete(`/destinations/${destination.id}`);
      onSuccess();
      messagesStore.showSuccessMessage('Destination deleted successfully');
      this.destinations = this.destinations.filter(
        (existingDest) => existingDest.id !== destination.id,
      );
      transformationsListStore.getTransformations();
      Analytics.track({
        event: 'destinationDeleted',
        properties: {
          destinationId: destination.id,
          destinationType: destination.destinationDefinition.name,
        },
      });
    } catch (err) {
      onError?.();
      messagesStore.showErrorMessage('Failed to delete destination');
    }
  }

  @action.bound
  public destinationById(destId: string): IDestinationStore | undefined {
    return this.destinations.find((dest) => dest.id === destId);
  }

  public hasWareHouseDestinations() {
    return this.destinations.some(
      (destination: IDestinationStore) =>
        destination?.destinationDefinition.category === DESTINATION_DEFINITION_CATEGORY.WAREHOUSE,
    );
  }

  @action.bound
  public checkDestinationNameUniqueness(newName: string, currentName = '') {
    const input = newName.trim();

    if (input === currentName) {
      return true;
    }

    return !this.destinations.some((d) => d.name.toLowerCase() === input.toLowerCase());
  }

  // Returns the UI-config field keys to hide during the account migration transition.
  // During migration, the destination UI config temporarily contains both raw account credential fields
  // (optionFields / secretFields) and a rudderAccountId picker — only one set should render.
  public getKeysToRemove(
    destination: Pick<IDestination, 'destinationDefinition'> &
      Partial<Pick<IDestination, 'id' | 'config'>>,
  ): Set<string> {
    const supportedAccountTypes =
      destination.destinationDefinition.config.supportedAccountDefinitions?.rudderAccountId || [];

    // No supportedAccountDefinitions means this destination isn't part of account management;
    // the UI config has no extra fields, so nothing to remove.
    if (supportedAccountTypes.length === 0) {
      return new Set();
    }

    // Legacy OAuth destinations never have raw credential fields in their UI config,
    // so there is nothing to remove regardless of migration state.
    if (isOauthEnabledDest(destination?.destinationDefinition)) {
      return new Set();
    }

    // Collect all credential field keys from the matching account definitions.
    const accountFields = new Set<string>();
    const supportedAccountDefs =
      this.rootStore.accountDefinitionsListStore.accountDefinitions.filter((accountDef) =>
        supportedAccountTypes.includes(accountDef.name),
      );
    supportedAccountDefs.forEach((accountDef) => {
      const optionFields = accountDef.config?.optionFields || [];
      const secretFields = accountDef.config?.secretFields || [];
      [...optionFields, ...secretFields].forEach((key) => accountFields.add(key));
    });

    const accountIdFields = new Set(['rudderAccountId']);

    // Custom Activation renders authentication inside the builder: keep the account picker
    // available while hiding the raw credential fields.
    if (destination.destinationDefinition.name === CUSTOM_ACTIVATION_DESTINATION_DEFINITION_NAME) {
      return accountFields;
    }

    const isExistingDestination = !!destination?.id;
    if (isExistingDestination) {
      // Existing destination: presence of rudderAccountId indicates it has been migrated,
      // so hide the raw credential fields and show the accountId picker — and vice-versa.
      const migratedDestination = !!destination.config?.rudderAccountId;
      if (migratedDestination) {
        return accountFields;
      }
      return accountIdFields;
    }

    // New destination: infer migration state from sibling destinations of the same definition.
    // Migration is all-or-nothing per workspace, so one migrated sibling means all should use
    // the accountId picker.
    const siblingDestinations = this.destinations.filter(
      (dest) => dest.destinationDefinition.name === destination?.destinationDefinition.name,
    );
    // If no siblings exist yet, default to the account picker.
    if (siblingDestinations.length === 0) {
      return accountFields;
    }
    const anyMigrated = siblingDestinations.some((dest) => !!dest.config.rudderAccountId);
    return anyMigrated ? accountFields : accountIdFields;
  }

  @action.bound
  public reset() {
    this.setDestinations([]);
    this.firstLoad = false;
  }
}
